import React, { useCallback, useEffect, useMemo, useState } from "react";
import PlannerHeader from "./PlannerHeader";
import { useDispatch, useSelector } from "react-redux";
import {
  selectWeeklyPlan,
  selectWeeklyPlanIsLoading,
  updateWeeklyPlanDates,
} from "@/store/slices";
import { WeeklyPlanStateData } from "@/models";
import { useRosterInfo } from "@/hooks";
import { calculateEndDate } from "@/utilities";
import { CurriculumIcons } from "@/constants/CurriculumIcons";
import { useNavigate } from "react-router";
import { ModalPortal } from "@/components/Global";
import { LearningTimeModal } from "./LearningTimeModal";
import { getTimeAssignedByStudent } from "@/utilities";
import { SkillType } from "@/enums";
import { getMostCommonGrade } from "@/utilities/getTimeAssignedByGrade";
import WarningDiamond from "../icons/WarningDiamond";
import "./PlannerHeaderContainer.scss";

enum PlannerModalState {
  NONE,
  LEARNING_TIME,
  MASTERY,
  PLAYLIST,
}

interface PlannerHeaderContainerProps {
  isFullHeader: boolean;
  handleSetWeekLoading: (loading: boolean) => void;
}

const PlannerHeaderContainer: React.FC<PlannerHeaderContainerProps> = ({
  isFullHeader,
  handleSetWeekLoading
}) => {
  // - START FAKE DATA FOR DEV PURPOSES
  const weekNum = 11; // TODO need to retrieve from curriculum service maybe?
  const { data, skillsMasteryLoaded } = useSelector(selectWeeklyPlan);
  const loading = useSelector(selectWeeklyPlanIsLoading);
  const { dateFrom, dateTo, curriculum, curTrack } =
    data as WeeklyPlanStateData;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(new Date(`${dateFrom}T00:00:00`));
  const [endDate, setEndDate] = useState(new Date(`${dateTo}T00:00:00`));
  const [week, setWeek] = useState(weekNum);
  const [weekLoading, setWeekLoading] = useState(false);
  const { students } = useRosterInfo();
  const [modalState, setModalState] = useState<PlannerModalState>(() => {
    const savedState = sessionStorage.getItem("PlannerModalState");
    return savedState ? JSON.parse(savedState) : PlannerModalState.NONE;
  });

  const nextWeek = useCallback(() => {
    const nextWeekStart = calculateEndDate(7, startDate);
    setStartDate(nextWeekStart);
    setEndDate(calculateEndDate(4, nextWeekStart));
    setWeek(week + 1); //WEEK DATA should be coming from the backend
    setWeekLoading(true);
    handleSetWeekLoading(true);
  }, [startDate, calculateEndDate, week]);

  const prevWeek = useCallback(() => {
    const prevWeekStart = calculateEndDate(-7, startDate);
    setStartDate(prevWeekStart);
    setEndDate(calculateEndDate(4, prevWeekStart));
    setWeek(week - 1); //WEEK DATA should be coming from the backend
    setWeekLoading(true);
    handleSetWeekLoading(true);
  }, [startDate, calculateEndDate, week]);

  const handleStudentPlaylistPress = () => {
    navigate(`/planner/student-learning-playlist`);
  };

  const handlePlannerPress = () => {
    navigate(`/planner`);
  };

  const onLearningTimeClick = () =>
    setModalState(PlannerModalState.LEARNING_TIME);
  const onLearningTimeClose = () => setModalState(PlannerModalState.NONE);

  // debounce the week change dispatch
  useEffect(() => {
    const timer = setTimeout(() => {
      const lessonStartDate = new Date(`${dateFrom}T00:00:00`);
      if (lessonStartDate.getTime() !== startDate.getTime()) {
        dispatch(
          updateWeeklyPlanDates({
            dateFrom: startDate.toISOString().split("T")[0],
            dateTo: calculateEndDate(4, startDate).toISOString().split("T")[0],
          })
        );
      }
      setWeekLoading(false);
      handleSetWeekLoading(false);
    }, 1000); // 1-second debounce delay

    // Cleanup the timeout if the component unmounts or inputValue changes
    return () => clearTimeout(timer);
  }, [startDate]);

  // Side effects
  useEffect(() => {
    sessionStorage.setItem("PlannerModalState", JSON.stringify(modalState));
  }, [modalState]);

  const getTotalAlerts = useCallback(() => {
    if (!data?.curriculum?.tracks) return 0;

    const totalTimeAssignedByStudent: Record<string, number> = {};
    
    // Calculate class predominant grade once
    const classGrade = (getMostCommonGrade(students) ?? 1).toString();

    for (const student of students) {
      for (const track of data.curriculum.tracks) {
        for (const unit of track.units) {
          for (const lesson of unit.lessons) {
            const skills = lesson.skills.filter(skill => skill.skillType === SkillType.FOCUS);
            skills.forEach(skill => {
              const time = getTimeAssignedByStudent(lesson, skill, student, data?.mastery, false, classGrade);
              if (totalTimeAssignedByStudent[student.id]) {
                totalTimeAssignedByStudent[student.id] += time;
              } else {
                totalTimeAssignedByStudent[student.id] = time;
              }
            });
          }
        }
      }
    }
    const alertCount = Object.values(totalTimeAssignedByStudent).filter(time => time > 45).length;
    return alertCount;
  }, [data]);

  const { hasMultipleGrades, mostCommonGrade } = useMemo(() => {
    const uniqueGrades = new Set(students.map((student: any) => student.grade));
    const hasMultipleGrades = uniqueGrades.size > 1;
    const mostCommonGrade = hasMultipleGrades ? getMostCommonGrade(students) : null;
    
    return { hasMultipleGrades, mostCommonGrade };
  }, [students]);

  const MultiGradeMessage: React.FC = () => {
    if (!hasMultipleGrades) return null;
    
    let helpArticleUrl = "https://help.amiralearning.com/s/article/How-AI-Lesson-Planner-Works-in-Multi-Grade-Classrooms";
    
    return (
      <div className="d-flex gap-2 justify-content-center align-items-center mb-3 mt-2 px-3">
        <div className="multi-grade-message-text">
          <WarningDiamond />
          This class has students in multiple grades. Lessons are displayed for grade {mostCommonGrade}.{' '}
          <a 
            href={helpArticleUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="multi-grade-message-link"
            >
            Learn more
          </a>
          .
        </div>
      </div>
    );
  };

  return (
    <>
      <PlannerHeader
        curriculumIcon={
          curriculum.name
            ? (CurriculumIcons[
                curriculum.name.toLowerCase().replaceAll(" ", "_")
              ] ?? CurriculumIcons["default"])
            : ""
        }
        curriculumName={curriculum.name}
        loading={loading || weekLoading}
        disableWeekSwitching={loading}
        weekNum={week}
        dateFrom={startDate}
        dateTo={endDate}
        prevWeekHandler={prevWeek}
        nextWeekHandler={nextWeek}
        handleStudentPlaylistPress={handleStudentPlaylistPress}
        handlePlannerPress={handlePlannerPress}
        onLearningTimeClick={onLearningTimeClick}
        isFullHeader={isFullHeader}
        totalBookingAlerts={getTotalAlerts()}
      />
      <MultiGradeMessage />
      <ModalPortal isOpen={modalState === PlannerModalState.LEARNING_TIME}>
        <ModalPortal.Header
          title={"Manage Assignments to Build Mastery"}
          onClose={onLearningTimeClose}
        />
        <LearningTimeModal />
      </ModalPortal>
    </>
  );
};

export default PlannerHeaderContainer;
